import classNames from "classnames";
import {useState} from "react";
import {FiAlertTriangle, FiCheck, FiInfo} from "react-icons/fi";

import {Alert} from "@/components/alert/Alert.tsx";
import {Chip} from "@/components/chip/Chip.tsx";
import {Divider} from "@/components/divider/Divider.tsx";
import {ToggleButton} from "@/components/toggleButton/ToggleButton.tsx";
import {NetworkProbeResult} from "@/lib/types/network.ts";

import {formatSeconds, originLabel} from "./networkProbe.utils.ts";
import {NetworkProbeModalContext} from "./networkRunTestTypes.ts";
import {tracerouteBanner} from "./tracerouteHops.utils.ts";
import {TracerouteHopsTable} from "./TracerouteHopsTable.tsx";
import {TracerouteRawOutput} from "./TracerouteRawOutput.tsx";

import styles from "./NetworkProbeDetail.module.scss";

const responseText = (probe: NetworkProbeResult) => {
  // A connection-level failure (DNS/timeout/…) has no usable HTTP response, so
  // surface its reason as the response instead of a meaningless "HTTP 0".
  if (!probe.status_code && probe.error) {
    return probe.error;
  }
  const code = probe.status_code === null ? "—" : probe.status_code;
  return `HTTP ${code} in ${formatSeconds(probe.response_time_ms)}`;
};

export const NetworkProbeDetail = ({
  probe,
  context,
}: {
  probe: NetworkProbeResult;
  context: NetworkProbeModalContext;
}) => {
  const [tab, setTab] = useState<"hops" | "raw">("hops");
  const trace = probe.traceroute;
  const pass = probe.status === "PASS";
  const hasHops = !!trace && trace.hops.length > 0;
  const banner = hasHops ? tracerouteBanner(trace!.hops) : null;

  return (
    <div>
      <div className={styles.detailHeader}>
        <Chip variant={pass ? "success" : "danger"} size="small">
          {pass ? "Passed" : "Failed"}
        </Chip>
        <span className={styles.detailTitle}>{originLabel(probe.origin)}</span>
        {context === "alert" && probe.recorded_at && (
          <span className={styles.detailTimestamp}>{probe.recorded_at}</span>
        )}
      </div>

      <div className={styles.metaRow}>
        <div>
          <div className={styles.metaLabel}>Network</div>
          <div className={styles.metaValue}>
            {probe.origin.network} AS{probe.origin.asn}
          </div>
        </div>
        <div>
          <div className={styles.metaLabel}>Response</div>
          <div className={classNames(styles.metaValue, {[styles.metaValueError]: !pass})}>{responseText(probe)}</div>
        </div>
      </div>

      {hasHops && banner ? (
        <>
          <Alert
            variant={banner.reached ? (pass ? "verification" : "default") : "error"}
            icon={banner.reached ? (pass ? FiCheck : FiInfo) : FiAlertTriangle}
            typography="bodySmall"
          >
            {banner.text}
          </Alert>

          <Divider className={styles.sectionDivider} />

          <div className={styles.tracerouteBar}>
            <span>
              <span className={styles.tracerouteLabel}>Traceroute</span>
              {probe.resolved_addr && <span className={styles.resolvedTo}>Resolved to {probe.resolved_addr}</span>}
            </span>
            <ToggleButton
              variant="segmented"
              items={[
                {id: "hops", label: "Hops"},
                {id: "raw", label: "Raw output"},
              ]}
              selectedId={tab}
              onChange={(id) => setTab(id as "hops" | "raw")}
            />
          </div>

          {tab === "hops" ? (
            <TracerouteHopsTable hops={trace!.hops} />
          ) : (
            <TracerouteRawOutput text={trace!.raw_output} />
          )}
        </>
      ) : trace ? (
        // Traceroute captured but no hops parsed — show the notice only, no table.
        <Alert variant="default" icon={FiInfo} typography="bodySmall">
          No traceroute hops were recorded
        </Alert>
      ) : (
        <Alert variant="default" icon={FiInfo} callout="Traceroute Not Enabled" typography="bodySmall">
          {context === "alert"
            ? "This check did not have Traceroute enabled when this result was recorded, so no network path was captured. Enabling it captures a path on every run from then on — it cannot be applied to past alerts."
            : "This probe did not capture a network path. Enable “Capture traceroute” on the check to record hop-by-hop data."}
        </Alert>
      )}
    </div>
  );
};
